import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useUnread } from "@/context/UnreadContext";
import { API } from "@/lib/api";
import { logDev } from "@/lib/log";

/**
 * Connects to /api/stream (SSE) and toasts new announcements / signals in real time.
 * Auth is carried by the httpOnly `access_token` cookie — EventSource forwards it
 * because we set `withCredentials: true` (same-origin ingress).
 */
export default function NotificationStream({ onSignal, onAnnouncement }) {
  const { user } = useAuth();
  const { bump } = useUnread();
  const esRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const es = new EventSource(`${API}/stream`, { withCredentials: true });
    esRef.current = es;

    es.addEventListener("announcement", (evt) => {
      try {
        const data = JSON.parse(evt.data);
        toast(data.title, { description: data.message });
        bump("announcement");
        onAnnouncement && onAnnouncement(data);
      } catch (err) { logDev(err); }
    });

    es.addEventListener("signal", (evt) => {
      try {
        const data = JSON.parse(evt.data);
        toast.success(`New Signal · ${data.pair} ${data.direction.toUpperCase()}`, {
          description: `TP ${data.tp} · SL ${data.sl}`,
        });
        bump("signal");
        onSignal && onSignal(data);
      } catch (err) { logDev(err); }
    });

    es.onerror = () => { /* browsers auto-reconnect */ };

    return () => es.close();
  }, [user, onSignal, onAnnouncement, bump]);

  return null;
}
